import * as Sentry from '@sentry/react';
import { z } from 'zod';

import { CurationPlatformAppId } from './curationPlatformApps';

const curationPlatformAppUrlSchema = z.url({ protocol: /^https?$/ });

const getRawCurationPlatformAppUrl = (appId: CurationPlatformAppId): string | undefined => {
  switch (appId) {
    case CurationPlatformAppId.annotationTool:
      return import.meta.env.VITE_ANNOTATION_TOOL_URL;
    case CurationPlatformAppId.accuracyDashboard:
      return import.meta.env.VITE_ACCURACY_DASHBOARD_URL;
    default:
      return undefined;
  }
};

/**
 * Resolves the other Curation Platform app's absolute URL for the current
 * environment. Reads the env var inside the function (not at module scope)
 * so it stays stubbable in tests. Returns null — and reports to Sentry —
 * when the var is unset or isn't an absolute http(s) URL, since the caller's
 * fallback (an omitted nav row, or the no-access message instead of a
 * redirect) would otherwise be indistinguishable from the user not being
 * entitled.
 */
export const getCurationPlatformAppUrl = (appId: CurationPlatformAppId): string | null => {
  const result = curationPlatformAppUrlSchema.safeParse(getRawCurationPlatformAppUrl(appId));

  if (!result.success) {
    Sentry.captureMessage(
      `Curation Platform: URL for "${appId}" is missing or not an absolute http(s) URL — omitting its nav row / cross-app redirect`
    );
    return null;
  }

  return result.data;
};
